/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { jsx, css } from '@compiled/react';

import { ZERO_WIDTH_SPACE } from '@atlaskit/editor-common/whitespace';
import { Popup } from '@atlaskit/popup';
import { LazyConfluenceObjectFinalizePopup } from '@atlassian/conversation-assistant/ui/staging-area-modal/lazy-confluence-object-finalize-popup';

import { useEditorRemixContext } from './context';
import { getRemixIdFromMessage, getRemixUrl, insertEmbedIntoPage } from './utils/message';

const triggerStyles = css({
	position: 'fixed',
	top: '25%',
	left: '50%',
});

export function LocationPickerPopup(): React.JSX.Element | null {
	const {
		entryPoint,
		conversationId,
		showLocationPicker,
		setShowLocationPicker,
		smartCreationConfig,
		smartCreationContext,
		finalMessage,
		resetContext,
	} = useEditorRemixContext();
	const action = finalMessage?.content?.message?.message?.actions?.[0];
	const actionKey = action?.key;
	const spaceId = smartCreationContext?.confluence?.space?.id;
	const remixId = getRemixIdFromMessage(finalMessage);
	const remixUrl = getRemixUrl(remixId, actionKey, spaceId);
	const currentPageId = smartCreationContext?.confluence?.content?.id;
	const insertReceiverId = currentPageId ?? smartCreationContext?.editor?.editorId;

	/**
	 * Called by the picker after a successful user-driven finalization.
	 * We discard the popup's baseUrl/path because the user is already in the
	 * editor — we just need to insert the embed and dismiss the modal.
	 */
	const handlePopupFinalizeSuccess = useCallback(
		(_params: {
			baseUrl: string;
			path: string;
			showFlagProps?: { createdIssueKeys: string[] };
		}) => {
			if (remixUrl) {
				insertEmbedIntoPage({ url: remixUrl, insertReceiverId });
			}
			resetContext();
			setShowLocationPicker(false);
		},
		[remixUrl, insertReceiverId, setShowLocationPicker, resetContext],
	);

	if (
		!entryPoint ||
		!smartCreationConfig ||
		!smartCreationContext ||
		!finalMessage ||
		!remixId ||
		!remixUrl ||
		!conversationId
	) {
		return null;
	}

	return (
		<Popup
			shouldRenderToParent
			isOpen={showLocationPicker}
			onClose={() => {
				// Picker dismissed without finalizing — close the modal, don't insert the embed.
				resetContext();
				setShowLocationPicker(false);
			}}
			placement="bottom-end"
			content={() => (
				<LazyConfluenceObjectFinalizePopup
					config={smartCreationConfig}
					contentId={remixId ?? ''}
					closeModal={handlePopupFinalizeSuccess}
					closePopup={() => {
						resetContext();
						setShowLocationPicker(false);
					}}
					conversationId={conversationId}
					entryPoint={entryPoint}
					context={smartCreationContext}
					/* Required so ConfluenceObjectFinalizePopup can call resolveConversationAction
					 * when aifc_finalize_convo_ai is on — without it the action resolution is silently skipped. */
					selectedPreviewMessage={finalMessage}
				/>
			)}
			trigger={(triggerProps) => (
				<span
					data-testid="remix-location-picker-popup-trigger"
					{...triggerProps}
					css={triggerStyles}
				>
					{ZERO_WIDTH_SPACE}
				</span>
			)}
		/>
	);
}
